import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { ref, readonly, computed, inject } from "vue";
import { useToastNotificationStore } from "@/experiences/toast-notifications/store/toast-notifications";
import { useContext } from "@/store/context";
import ExternalContentApi from "@/api/external-content";
import { MIN_DURATION_SECONDS, MAX_DURATION_SECONDS, DEFAULT_LIVE_MINUTES_RATIO, PENDING_DELETE_TOAST_KEY } from "../constants";
export const useExternalContentStore = defineStore("external-content", () => {
    let i18n;
    const { currentStudyId } = storeToRefs(useContext());
    const { addErrorNotice, addSuccessNotice } = useToastNotificationStore();
    // Initialization params
    const activityId = ref(0);
    // Config state
    const config = ref({
        enabled: false,
        maxFileSize: 0,
        allowedExtensions: [],
        minDurationSeconds: MIN_DURATION_SECONDS,
        maxDurationSeconds: MAX_DURATION_SECONDS,
        supportedLanguages: [],
        liveMinuteBalance: 0,
        liveMinutesRatio: DEFAULT_LIVE_MINUTES_RATIO,
        hasAcknowledgedLiveMinutes: false
    });
    const configPending = ref(false);
    const configError = ref(null);
    // Content state
    const contents = ref([]);
    const contentPending = ref(false);
    const contentError = ref(null);
    // Guards against stale refresh responses overwriting fresh data
    let refreshVersion = 0;
    // Getters
    const hasContent = computed(() => contents.value.length > 0);
    const init = (aid) => {
        i18n = inject("i18n");
        activityId.value = aid;
        if (sessionStorage.getItem(PENDING_DELETE_TOAST_KEY)) {
            sessionStorage.removeItem(PENDING_DELETE_TOAST_KEY);
            addSuccessNotice(i18n.t("File has been deleted."));
        }
    };
    const getConfig = () => {
        if (currentStudyId.value === undefined) {
            console.warn("Unable to fetch config. Current study ID is missing.");
            return;
        }
        configPending.value = true;
        configError.value = null;
        ExternalContentApi.getConfig(currentStudyId.value, activityId.value)
            .then((res) => {
            config.value = res;
        })
            .catch((err) => {
            configError.value = err instanceof Error ? err.message : i18n.t("Failed to load external content config");
            addErrorNotice(configError.value);
        })
            .finally(() => {
            configPending.value = false;
        });
    };
    const refresh = () => {
        if (currentStudyId.value === undefined) {
            console.warn("Unable to refresh. Current study ID is missing.");
            return;
        }
        contentPending.value = true;
        contentError.value = null;
        const version = ++refreshVersion;
        ExternalContentApi.list(currentStudyId.value, activityId.value)
            .then((res) => {
            if (version === refreshVersion)
                contents.value = res;
        })
            .catch((err) => {
            if (version !== refreshVersion)
                return;
            contentError.value = err instanceof Error ? err.message : i18n.t("Failed to load content");
            addErrorNotice(contentError.value);
        })
            .finally(() => {
            if (version === refreshVersion)
                contentPending.value = false;
        });
    };
    const refreshItem = (contentId) => {
        if (currentStudyId.value === undefined) {
            console.warn("Unable to fetch item. Current study ID is missing.");
            return;
        }
        ExternalContentApi.getById(currentStudyId.value, activityId.value, contentId)
            .then((updated) => {
            const index = contents.value.findIndex((c) => c.externalContentId === contentId);
            if (index >= 0) {
                Object.assign(contents.value[index], updated);
            }
        })
            .catch((err) => {
            contentError.value = err instanceof Error ? err.message : i18n.t("Failed to refresh item");
            addErrorNotice(contentError.value);
        });
    };
    const remove = (contentId, options) => {
        if (currentStudyId.value === undefined) {
            console.warn("Unable to remove item. Current study ID is missing.");
            return;
        }
        const index = contents.value.findIndex((c) => c.externalContentId === contentId);
        if (index === -1)
            return;
        const removed = contents.value[index];
        // Replace with a new array to guarantee Vue reactivity propagates through
        // readonly proxies and PrimeVue DataTable re-renders immediately.
        contents.value = contents.value.filter((c) => c.externalContentId !== contentId);
        ExternalContentApi.remove(currentStudyId.value, activityId.value, contentId)
            .then(() => {
            if (!options?.silent) {
                addSuccessNotice(i18n.t("File has been deleted."));
            }
        })
            .catch((err) => {
            const restored = [...contents.value];
            restored.splice(index, 0, removed);
            contents.value = restored;
            contentError.value = err instanceof Error ? err.message : i18n.t("Failed to delete content");
            addErrorNotice(contentError.value);
        });
    };
    const create = async (data) => {
        if (currentStudyId.value === undefined) {
            console.warn("Unable to create item. Current study ID is missing.");
            return;
        }
        try {
            return await ExternalContentApi.create(currentStudyId.value, activityId.value, data);
        }
        catch (err) {
            contentError.value = err instanceof Error ? err.message : i18n.t("Failed to create content");
            addErrorNotice(contentError.value);
            throw err;
        }
    };
    const updateMetadata = async (files) => {
        if (currentStudyId.value === undefined) {
            console.warn("Unable to update metadata. Current study ID is missing.");
            return;
        }
        const filesToUpdate = files.filter((f) => f.externalContentId != null);
        return Promise.all(filesToUpdate.map((file) => ExternalContentApi.updateMetadata(currentStudyId.value, activityId.value, file.externalContentId, {
            displayName: file.displayName,
            sourceDate: file.sourceDate || undefined,
            contentMode: file.contentMode,
            hostCount: file.hostCount,
            language: file.language,
            frontendDurationEstimateMs: file.duration != null ? Math.round(file.duration * 1000) : undefined
        }).catch((err) => {
            contentError.value =
                err instanceof Error ? err.message : i18n.t("Failed to update metadata for {0}", [file.externalContentId]);
            addErrorNotice(contentError.value);
        }))).finally(() => {
            if (filesToUpdate.length > 0)
                refresh();
        });
    };
    const updateContentMetadata = async (contentId, data) => {
        if (currentStudyId.value === undefined) {
            console.warn("Unable to update metadata. Current study ID is missing.");
            return;
        }
        return ExternalContentApi.updateMetadata(currentStudyId.value, activityId.value, contentId, data)
            .then((updated) => {
            const index = contents.value.findIndex((c) => c.externalContentId === contentId);
            if (index >= 0)
                Object.assign(contents.value[index], updated);
        })
            .catch((err) => {
            contentError.value = err instanceof Error ? err.message : i18n.t("Failed to update metadata");
            addErrorNotice(contentError.value);
        });
    };
    const getById = (contentId) => {
        return contents.value.find((c) => c.externalContentId === contentId);
    };
    const acknowledgeLiveMinutes = async () => {
        if (currentStudyId.value === undefined) {
            console.warn("Unable to acknowledge live minutes. Current study ID is missing.");
            return;
        }
        configPending.value = true;
        configError.value = null;
        return ExternalContentApi.acknowledgeLiveMinutes(currentStudyId.value, activityId.value)
            .then(() => {
            config.value.hasAcknowledgedLiveMinutes = true;
        })
            .catch((err) => {
            console.error("[ExternalContent] Failed to acknowledge live minutes:", err);
            configError.value = err instanceof Error ? err.message : i18n.t("Failed to acknowledge live minutes");
            addErrorNotice(configError.value);
        })
            .finally(() => {
            configPending.value = false;
        });
    };
    /**
     * Sync activity dates to encompass all content source dates.
     */
    const syncActivityDates = () => {
        if (currentStudyId.value === undefined) {
            console.warn("Unable to sync activity dates. Current study ID is missing.");
            return;
        }
        return ExternalContentApi.syncDates(currentStudyId.value, activityId.value).catch((err) => {
            contentError.value = err instanceof Error ? err.message : i18n.t("Failed to sync activity dates");
            addErrorNotice(contentError.value);
        });
    };
    return {
        config: readonly(config),
        configPending: readonly(configPending),
        configError: readonly(configError),
        contents: readonly(contents),
        contentPending: readonly(contentPending),
        contentError: readonly(contentError),
        hasContent,
        init,
        getConfig,
        refresh,
        refreshItem,
        create,
        remove,
        updateMetadata,
        updateContentMetadata,
        getById,
        acknowledgeLiveMinutes,
        syncActivityDates
    };
});
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useExternalContentStore, import.meta.hot));
}
